import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/http';
import { authApi } from '@/api/endpoints';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/dashboard/mensagens/thread')({
  validateSearch: (s: Record<string, unknown>) => ({
    clientId: typeof s.clientId === 'string' ? s.clientId : '',
    therapistId: typeof s.therapistId === 'string' ? s.therapistId : '',
  }),
  component: ThreadPage,
});

function ThreadPage() {
  const { clientId, therapistId } = useSearch({ from: '/dashboard/mensagens/thread' });
  const qc = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState('');

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: authApi.me });

  const { data: messages = [] } = useQuery({
    queryKey: ['chat-thread', clientId, therapistId],
    queryFn: () =>
      api.get('chat/messages', { searchParams: { clientId, therapistId } }).json<any[]>(),
    refetchInterval: 5000,
    enabled: !!clientId && !!therapistId,
  });

  const { data: threads = [] } = useQuery({
    queryKey: ['chat-threads'],
    queryFn: () => api.get('chat/threads').json<any[]>(),
  });
  const thread = threads.find((t: any) => t.clientId === clientId && t.therapistId === therapistId);

  // Fallback quando thread ainda não existe (primeiro contato): busca o
  // terapeuta pra mostrar nome/foto no header.
  const { data: therapistFallback } = useQuery({
    queryKey: ['therapist-public', therapistId],
    queryFn: () => api.get(`therapists/${therapistId}`).json<any>(),
    enabled: !!therapistId && !thread,
  });
  const peer = thread?.peer ?? (therapistFallback
    ? { name: therapistFallback.user?.name, image: therapistFallback.user?.image, role: 'THERAPIST' }
    : null);

  const send = useMutation({
    // receiverId é derivado no backend a partir de clientId+therapistId+sender
    mutationFn: (content: string) =>
      api.post('chat/messages', {
        json: { content, clientId, therapistId },
      }).json<any>(),
    onSuccess: () => {
      setText('');
      qc.invalidateQueries({ queryKey: ['chat-thread', clientId, therapistId] });
      qc.invalidateQueries({ queryKey: ['chat-threads'] });
    },
  });

  const markRead = useMutation({
    mutationFn: () =>
      api.post('chat/threads/read', { json: { clientId, therapistId } }).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat-threads'] }),
  });

  // Marca como lida ao entrar
  useEffect(() => {
    if (clientId && therapistId) markRead.mutate();
  }, [clientId, therapistId]); // eslint-disable-line

  // Auto-scroll para o fim quando chegam novas mensagens
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    send.mutate(t);
  }

  return (
    <div className="max-w-3xl space-y-4 h-[calc(100vh-8rem)] flex flex-col">
      <div className="flex items-center gap-3">
        <Link to="/dashboard/mensagens" className="text-sm text-teal-600 hover:underline">
          ← Voltar
        </Link>
      </div>

      <Card className="flex-1 flex flex-col overflow-hidden">
        <header className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
          {peer?.image ? (
            <img src={peer.image} alt="" className="size-10 rounded-full object-cover" />
          ) : (
            <div className="size-10 rounded-full bg-gradient-to-br from-teal-300 to-coral-300 flex items-center justify-center text-white font-bold">
              {(peer?.name ?? '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <p className="font-semibold text-slate-800">{peer?.name ?? '...'}</p>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">
              {peer?.role === 'THERAPIST' ? 'Profissional' : 'Cliente'}
            </p>
          </div>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50/40">
          {messages.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-8">Nenhuma mensagem ainda. Diga oi.</p>
          ) : (
            messages.map((m: any) => {
              const mine = m.senderId === me?.id;
              return (
                <div key={m.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
                  <div
                    className={cn(
                      'max-w-[75%] rounded-2xl px-3.5 py-2 text-sm shadow-sm',
                      mine
                        ? 'bg-teal-500 text-white rounded-br-sm'
                        : 'bg-white text-slate-800 rounded-bl-sm border border-slate-200',
                    )}
                  >
                    <p className="whitespace-pre-wrap break-words">{m.content}</p>
                    <p className={cn('text-[10px] mt-1', mine ? 'text-teal-50' : 'text-slate-400')}>
                      {new Date(m.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-3 border-t border-slate-100 flex items-end gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            rows={1}
            placeholder="Escreva uma mensagem..."
            className="flex-1 resize-none bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 max-h-32"
          />
          <Button type="submit" disabled={!text.trim() || send.isPending} className="bg-teal-500 hover:bg-teal-600 text-white">
            Enviar
          </Button>
        </form>
      </Card>
    </div>
  );
}
